import { antdNotification } from '@hellodarwin/core/lib/features/providers/antd-app-provider';
import PageLayout from '@hellodarwin/app-core/lib/layouts/page-layout';
import PdfViewer from '@hellodarwin/app-features/lib/features/pdfViewer/pdf-viewer';
import { AssetDownloadResponse } from '@hellodarwin/core/lib/features/entities/assets-entities';
import { useTranslations } from '@hellodarwin/core/lib/features/providers/translations-provider';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../app/app-hooks';
import { RootState } from '../../app/app-store';
import { downloadAsset } from '../../features/api/slices/assets-slice';
import {
  fetchGinApplication,
  selectGinApplicationById,
} from '../../features/api/slices/programs-slice';
import {
  useClientApi,
  useNewClientApi,
} from '../../features/api/use-client-api';

const ProgramPdfViewerPage = () => {
  const { t } = useTranslations();
  const api = useClientApi();
  const newApi = useNewClientApi();
  const dispatch = useAppDispatch();
  const { programId } = useParams<{ programId: string }>();
  const { assetId } = useParams<{ assetId: string }>();
  const navigate = useNavigate();

  const application = useAppSelector((state: RootState) =>
    programId ? selectGinApplicationById(state, programId) : undefined,
  );
  const [assetResponse, setAssetResponse] =
    useState<AssetDownloadResponse | null>(null);
  const applicationDisplayName =
    application?.application_name ||
    application?.application_company_name ||
    '';

  const handleDownload = useCallback(() => {
    if (!assetId) return;
    dispatch(downloadAsset({ api: newApi, asset_id: assetId }))
      .unwrap()
      .then((payload) => {
        window.open(payload.link, 'download');
      })
      .catch((error) => {
        console.error(error);
      });
  }, [dispatch, newApi, assetId]);

  useEffect(() => {
    if (!programId || !assetId) return;
    dispatch(fetchGinApplication({ api, applicationId: programId }));

    dispatch(downloadAsset({ api: newApi, asset_id: assetId }))
      .unwrap()
      .then((payload) => {
        setAssetResponse(payload);
      })
      .catch((error) => {
        antdNotification.error({
          message: t('errors|pdf_error'),
          placement: 'bottomLeft',
        });
        navigate(`/applications/${programId}`);
        console.error(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <PageLayout
      app="client"
      title={assetResponse?.asset.display_name}
      breadcrumbs={[
        {
          breadcrumbName: 'Home',
          path: '/',
        },
        {
          breadcrumbName: t('application_list|applications'),
          path: '/applications',
        },
        {
          breadcrumbName: applicationDisplayName,
          path: `/applications/${programId}`,
        },
        {
          breadcrumbName: assetResponse?.asset.display_name,
        },
      ]}
    >
      {assetResponse && assetId && (
        <PdfViewer
          pdfFile={assetResponse.link}
          onDownload={handleDownload}
          onError={() => navigate(`/applications/${programId}`)}
        />
      )}
    </PageLayout>
  );
};

export default ProgramPdfViewerPage;
